export interface AchievementDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  tier: 'milestone' | 'completion' | 'engagement';
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: 'first_birdie', name: 'First Birdie', icon: '🐦', desc: 'Made your first birdie of the year', tier: 'milestone' },
  { id: 'first_eagle', name: 'First Eagle', icon: '🦅', desc: 'Made your first eagle of the year', tier: 'milestone' },
  { id: 'first_ace', name: 'First Ace', icon: '🎯', desc: 'Aced a hole for the first time this year', tier: 'milestone' },
  { id: 'first_sub_80', name: 'First Sub-80', icon: '🔥', desc: 'Posted your first round under 80', tier: 'milestone' },
  { id: 'first_sub_par', name: 'First Under-Par Round', icon: '⚡', desc: 'Posted your first round below par', tier: 'milestone' },
  { id: 'first_even_par', name: 'First Even-Par Round', icon: '🎖️', desc: 'Carded your first even-par round', tier: 'milestone' },
  { id: 'first_beat_3', name: 'Net Statement Round', icon: '💎', desc: 'First round beating your handicap by 3+ strokes', tier: 'milestone' },
  { id: 'first_sub_par_9', name: 'Sub-Par Nine', icon: '🚀', desc: 'Played a 9-hole stretch under par for the first time', tier: 'milestone' },
  { id: 'arl_complete', name: 'Arlington Triple', icon: '🏛️', desc: 'Played all three Arlington combos', tier: 'completion' },
  { id: 'ffx_complete', name: 'Fairfax Triple', icon: '🏛️', desc: 'Played all three Fairfax combos', tier: 'completion' },
  { id: 'ancc_complete', name: 'ANCC Six', icon: '🏆', desc: 'Played all six ANCC course combinations', tier: 'completion' },
  { id: 'first_post', name: 'First Round Posted', icon: '⛳', desc: 'Posted your first round in Bag Tag', tier: 'engagement' },
  { id: 'ten_rounds', name: 'Ten Rounds This Year', icon: '📅', desc: 'Posted 10 rounds this year', tier: 'engagement' },
  { id: 'twenty_rounds', name: 'Twenty Rounds', icon: '📈', desc: 'Posted 20 rounds this year', tier: 'engagement' },
];

export function getAchievementDef(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}

export interface RoundForAchievement {
  member_id: string;
  gross_total: number | null;
  holes: string;
  course_slug: string | null;
  par: number;
  hole_scores: { hole_num: number; strokes: number; par: number }[];
  posted_handicap: number | null;
  played_at: string;
}

export interface AceForAchievement {
  member_id: string;
  played_at: string;
}

export function checkAchievements(
  memberId: string,
  rounds: RoundForAchievement[],
  aces: AceForAchievement[],
  already: { achievement_id: string; year: number }[],
): string[] {
  const thisYear = new Date().getFullYear();
  const myRounds = rounds.filter((r) => r.member_id === memberId);
  const myAces = aces.filter((a) => a.member_id === memberId);
  const thisYearRounds = myRounds.filter((r) => new Date(r.played_at).getFullYear() === thisYear);
  const thisYearAces = myAces.filter((a) => new Date(a.played_at).getFullYear() === thisYear);
  const alreadySet = new Set(
    already.filter((a) => a.year === thisYear).map((a) => a.achievement_id),
  );
  const unlocked: string[] = [];
  const add = (id: string) => { if (!alreadySet.has(id)) unlocked.push(id); };

  if (thisYearRounds.length >= 1) add('first_post');
  if (thisYearRounds.length >= 10) add('ten_rounds');
  if (thisYearRounds.length >= 20) add('twenty_rounds');
  if (thisYearAces.length >= 1) add('first_ace');

  for (const r of thisYearRounds) {
    if (r.gross_total != null && r.par != null) {
      const diff = r.gross_total - r.par;
      if (r.holes === 'full' && r.gross_total < 80) add('first_sub_80');
      if (r.holes === 'full' && diff < 0) add('first_sub_par');
      if (r.holes === 'full' && diff === 0) add('first_even_par');
    }
    if (r.hole_scores.length > 0) {
      for (const h of r.hole_scores) {
        const d = h.strokes - h.par;
        if (d === -1) add('first_birdie');
        if (d === -2) add('first_eagle');
      }
      const front = r.hole_scores.filter((h) => h.hole_num <= 9);
      const back = r.hole_scores.filter((h) => h.hole_num > 9);
      const par9 = (slice: typeof r.hole_scores) => (
        slice.length > 0 && slice.every((h) => Number.isFinite(h.strokes))
          ? slice.reduce((a, b) => a + b.strokes, 0) - slice.reduce((a, b) => a + b.par, 0)
          : null
      );
      const fdiff = par9(front);
      const bdiff = par9(back);
      if ((fdiff != null && fdiff < 0) || (bdiff != null && bdiff < 0)) add('first_sub_par_9');
    }
    if (r.gross_total != null && r.posted_handicap != null && r.par != null) {
      const net = r.gross_total - Math.round(r.posted_handicap);
      if (net - r.par <= -3) add('first_beat_3');
    }
  }

  const playedCourses = new Set(thisYearRounds.map((r) => r.course_slug).filter(Boolean));
  const arlIds = ['arl-br', 'arl-bw', 'arl-rw'];
  const ffxIds = ['ffx-br', 'ffx-bw', 'ffx-rw'];
  if (arlIds.every((id) => playedCourses.has(id))) add('arl_complete');
  if (ffxIds.every((id) => playedCourses.has(id))) add('ffx_complete');
  if ([...arlIds, ...ffxIds].every((id) => playedCourses.has(id))) add('ancc_complete');

  return unlocked;
}
